import moment from 'moment';
import { storageDateFormat, createDateObject, objectSet } from '@/core';
import { trip as tripStructure, } from '@/dataStructures';
import deepCopy from 'deepcopy';

function set(state, func) {
	if (func) {
		func(state);
		state.dirty = true;
	}
}

function setTripLoaded(state, tripLoaded) {
	state.tripLoaded = tripLoaded;
}

function setError(state, { path, value }) {
	if (path) {
		objectSet(state.errors, path, value);
	} else {
		state.errors = value;
	}
}

function setValue(state, { path, value }) {
	state.dirty = true;
	objectSet(state.trip, path, value);
}

function removeLeg(state, legNumber) {
	if (typeof legNumber !== 'number') throw new Error(`legNumber (${legNumber}) is not a number`);
	state.dirty = true;
	state.trip.legs.splice(legNumber, 1);
	for (let i = legNumber; i < state.trip.legs.length; i++) {
		state.trip.legs[i].legNumber = i;
	}
}

function swapLegOrder(state, { legNumberBefore, legNumberAfter }) {
	if (typeof (legNumberBefore) === 'undefined' || typeof (legNumberAfter) === 'undefined') {
		throw new Error(`swapLegOrder called with invalid arguments: ${legNumberBefore} -> ${legNumberAfter}`);
	}
	if (legNumberBefore < 0 || legNumberBefore >= state.trip.legs.length) throw new Error(`legNumberBefore (${legNumberBefore}) out of bounds`);
	if (legNumberBefore < 0 || legNumberAfter >= state.trip.legs.length) throw new Error(`legNumberAfter (${legNumberAfter}) out of bounds`);
	state.dirty = true;

	let leg1 = state.trip.legs.find(x => x.legNumber === legNumberBefore);
	let leg2 = state.trip.legs.find(x => x.legNumber === legNumberAfter);
	leg1.legNumber = legNumberAfter;
	leg2.legNumber = legNumberBefore;
	state.trip.legs.sort((a, b) => a.legNumber - b.legNumber);
}

function removeViaLocation(state, index) {
	state.dirty = true;
	state.trip.viaLocations.splice(index, 1);
	for (let i = index; i < state.trip.viaLocations.length; i++) {
		state.trip.viaLocations[i].viaNumber = i;
	}
}

// Creates new entry if viaLocations[index] is empty
function updateViaLocation(state, { update, index }) {
	if (typeof (index) === 'undefined' || typeof (update) === 'undefined') throw new Error('updateViaLocation called with invalid arguments');
	if (index < 0 || index > state.trip.viaLocations.length) throw new Error('updateViaLocation index out of bounds');
	state.dirty = true;
	state.trip.viaLocations.splice(
		index,
		1, // Delete 1 entry if it exists
		{ ...state.trip.viaLocations[index], ...update }); // New update object retains unchanged values
}

function swapViaLocationOrder(state, { viaNumberBefore, viaNumberAfter }) {
	if (typeof (viaNumberBefore) === 'undefined' || typeof (viaNumberAfter) === 'undefined') {
		throw new Error(`swapViaLocationOrder called with invalid arguments: ${viaNumberBefore} -> ${viaNumberAfter}`);
	}
	if (viaNumberBefore < 0 || viaNumberBefore >= state.trip.viaLocations.length) throw new Error(`viaNumberBefore (${viaNumberBefore}) out of bounds`);
	if (viaNumberBefore < 0 || viaNumberAfter >= state.trip.viaLocations.length) throw new Error(`viaNumberAfter (${viaNumberAfter}) out of bounds`);
	state.dirty = true;

	let viaLocation1 = state.trip.viaLocations.find(x => x.viaNumber === viaNumberBefore);
	let viaLocation2 = state.trip.viaLocations.find(x => x.viaNumber === viaNumberAfter);
	viaLocation1.viaNumber = viaNumberAfter;
	viaLocation2.viaNumber = viaNumberBefore;
	state.trip.viaLocations.sort((a, b) => a.viaNumber - b.viaNumber);
}

function setEditTrip(state, trip) {
	trip.viaLocations.sort((a, b) => a.viaNumber - b.viaNumber);
	trip.legs.sort((a, b) => a.legNumber - b.legNumber);

	let tripDataObject = deepCopy(trip);
	state.tripClone = {
		orders: [],
		legs: [],
		deviations: [],
		...deepCopy(trip),
	};

	state.trip = {
		...tripStructure(),
		...tripDataObject,
		id: tripDataObject.id,
		legs: tripDataObject.legs,
		orders: tripDataObject.orders
	};
	state.dirty = false;
	state.errors = { // TODO: Clean?
		invalidEtd: false,
		invalidEta: false,
		invalidFromAddress: false,
		invalidToAddress: false,
		invalidVendor: false,
	};
	state.errors = {};
}

function cleanState(state, currentClerk) {
	state.errors = {};
	state.dirty = false;

	state.trip = {
		...tripStructure(),
		clerk: currentClerk,
	};
	state.tripClone = deepCopy(state.trip);
}

function loadTemplate(state, template) {
	state.dirty = true;
	let clone = JSON.parse(JSON.stringify(template));
	for (let key in state.trip) {
		if (key !== 'id' && key in clone)
			state.trip[key] = clone[key];
	}
	state.trip.etd = createDateObject({
		date: moment().format(storageDateFormat),
		hasTime: Boolean(clone.departureTime),
		time: clone.departureTime
	});
	let date = moment().add(clone.duration, 'd').format(storageDateFormat);
	state.trip.eta = createDateObject({
		date,
		hasTime: Boolean(clone.arrivalTime),
		time: clone.arrivalTime
	});
}

function setTripChangelog(state, changelog) {
	state.changelog = changelog;
}

function setOrders(state, orders) {

	let sortedOrders = orders.slice();

	sortedOrders
		.sort( (o1, o2) => o1.loadingSeqNo - o2.loadingSeqNo );

	state.trip.orders = sortedOrders;
}

export default {
	set,
	setError,
	setValue,
	setEditTrip,
	cleanState,
	loadTemplate,
	updateViaLocation,
	removeViaLocation,
	swapViaLocationOrder,
	removeLeg,
	swapLegOrder,
	setTripLoaded,
	setTripChangelog,
	setOrders,
};